import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import { useMessageToast } from '../useMessageToast';
import { formatDateTime } from '../date';
import { Modal } from '../components/Modal';
import { useDialog } from '../dialog';

type ShareItem = {
  id: string;
  toolId: string;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  shareUrl: string;
};

type ShareDetail = ShareItem & {
  snapshotHtml: string;
  snapshotJson: string;
};

type ShareDetailResp = { ok: boolean } & ShareDetail;

function enhanceSnapshotHtmlForPreview(html: string) {
  const style = `<style>
  :root { color-scheme: dark; }
  html, body { margin: 0; padding: 0; background: #0b1020; color: rgba(255,255,255,0.92);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Microsoft YaHei', sans-serif; }
  a { color: inherit; }
  body { padding: 14px; }
  h1, h2, h3 { margin: 14px 0 8px; font-weight: 900; }
  hr { border: 0; border-top: 1px solid rgba(255,255,255,0.14); margin: 14px 0; }
  pre { white-space: pre-wrap; line-height: 1.75; padding: 12px 14px; border-radius: 14px;
    border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.06);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
  </style>`;

  const s = String(html || '');
  if (!s.trim()) return `<!doctype html><html><head>${style}</head><body></body></html>`;
  if (/<head[\s>]/i.test(s)) return s.replace(/<head[\s>][^>]*>/i, (m) => `${m}\n${style}\n`);
  if (/<html[\s>]/i.test(s)) return s.replace(/<html[\s>][^>]*>/i, (m) => `${m}\n<head>${style}</head>\n`);
  return `<!doctype html><html><head>${style}</head><body>${s}</body></html>`;
}

function fmt(ts: number | null) {
  if (!ts) return '-';
  return formatDateTime(ts) || new Date(ts).toLocaleString();
}

function JsonTree({
  value,
  name,
  depth
}: {
  value: any;
  name?: string;
  depth: number;
}) {
  const label = name ? <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 900 }}>{name}</span> : null;

  if (value === null) {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: 'rgba(255,255,255,0.70)' }}>null</span>
      </div>
    );
  }
  if (typeof value === 'string') {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: 'rgba(255,255,255,0.92)' }}>{JSON.stringify(value)}</span>
      </div>
    );
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: 'rgba(255,255,255,0.92)' }}>{String(value)}</span>
      </div>
    );
  }

  const isArr = Array.isArray(value);
  const keys = isArr ? value : Object.keys(value || {});
  const open = depth < 2;

  return (
    <details open={open} style={{ marginLeft: depth ? 14 : 0 }}>
      <summary style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.78)' }}>
        {label ? <span>{label}: </span> : null}
        <span>{isArr ? `Array(${keys.length})` : `Object(${keys.length})`}</span>
      </summary>
      <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
        {isArr
          ? value.map((v: any, i: number) => <JsonTree key={`${depth}-${i}`} name={String(i)} value={v} depth={depth + 1} />)
          : Object.entries(value || {}).map(([k, v]) => <JsonTree key={`${depth}-${k}`} name={k} value={v} depth={depth + 1} />)}
      </div>
    </details>
  );
}

export function SharesPanel() {
  const { prompt, confirmDelete, confirmRevoke } = useDialog();
  const [items, setItems] = useState<ShareItem[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  useMessageToast(msg, setMsg, 'sharespanel');
  const [detail, setDetail] = useState<ShareDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'preview' | 'json'>('preview');

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'REVOKE' | 'DELETE'>('REVOKE');
  const [bulkConfirm, setBulkConfirm] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = async () => {
    setMsg(null);
    const data = await apiFetch<{ ok: boolean; items: ShareItem[] }>('/api/shares', { method: 'GET' });
    setItems(data.items || []);
    setSelected({});
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg('已复制');
    } catch {
      await prompt({ title: '复制失败', message: '复制失败，请手动复制：', defaultValue: text, readOnly: true, showCancel: false, confirmText: '关闭' });
      setMsg('复制失败，请手动复制');
    }
  };

  useEffect(() => {
    load().catch(() => setItems([]));
  }, []);

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const selectedCount = selectedIds.length;
  const needConfirm = bulkAction === 'DELETE' ? 'DELETE' : 'CONFIRM';
  const confirmOk = bulkConfirm.trim().toUpperCase() === needConfirm;

  return (
    <div>
      <div className="section-title">
        <h3>分享管理</h3>
        <span className="muted">撤销后链接立即失效；删除会移除分享记录</span>
      </div>

      <div className="row" style={{ marginBottom: 12, justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn secondary"
            type="button"
            disabled={!items.length}
            onClick={() => {
              const next: Record<string, boolean> = {};
              const allSelected = items.every((it) => selected[it.id]);
              for (const it of items) next[it.id] = !allSelected;
              setSelected(next);
            }}
          >
            {items.length && items.every((it) => selected[it.id]) ? '取消全选' : '全选'}
          </button>
          <button className="btn secondary" disabled={!selectedCount} onClick={() => setBulkOpen(true)} type="button">
            批量操作（{selectedCount}）
          </button>
        </div>
        <button className="btn secondary" onClick={() => load().catch(() => setMsg('刷新失败'))} type="button">
          刷新
        </button>
      </div>

      <div className="tool-grid">
        {items.map((it) => {
          const expired = !!it.expiresAt && Date.now() > it.expiresAt;
          const revoked = !!it.revokedAt;
          const status = revoked ? '已撤销' : expired ? '已过期' : '有效';
          return (
            <div key={it.id} className="tool">
              <div className="tool-top">
                <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!selected[it.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <div className="tool-name">{it.toolId}</div>
                </div>
                <span className="badge">{status}</span>
              </div>
              <div className="muted">创建：{fmt(it.createdAt)}</div>
              <div className="muted">过期：{fmt(it.expiresAt)}</div>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      setMsg(null);
                      setDetailLoading(true);
                      try {
                        const d = await apiFetch<ShareDetailResp>(`/api/shares/${encodeURIComponent(it.id)}`, { method: 'GET' });
                        const { ok: _ok, ...rest } = d;
                        setDetail(rest);
                        setDetailTab('preview');
                      } catch {
                        setMsg('查看失败，请稍后重试');
                      } finally {
                        setDetailLoading(false);
                      }
                    }}
                  >
                    {detailLoading ? '加载中…' : '查看内容'}
                  </button>
                  <button className="btn secondary" type="button" onClick={() => copyText(it.shareUrl)}>
                    复制链接
                  </button>
                  <a className="btn secondary" href={it.shareUrl} target="_blank" rel="noreferrer">
                    打开链接
                  </a>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    className="btn danger"
                    type="button"
                    disabled={revoked}
                    onClick={async () => {
                      const ok = await confirmRevoke({ title: '撤销分享链接', message: '确认撤销该分享链接？撤销后链接立即失效' });
                      if (!ok) return;
                      try {
                        await apiFetch(`/api/shares/${encodeURIComponent(it.id)}/revoke`, { method: 'POST', body: JSON.stringify({}) });
                        await load();
                      } catch {
                        setMsg('撤销失败，请稍后重试');
                      }
                    }}
                  >
                    撤销
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      const ok = await confirmDelete({ title: '删除分享记录', message: '确认删除该分享记录？删除后无法恢复', confirmText: '确认删除' });
                      if (!ok) return;
                      try {
                        await apiFetch(`/api/shares/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                        await load();
                      } catch {
                        setMsg('删除失败，请稍后重试');
                      }
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!items.length ? <div className="muted">暂无分享记录</div> : null}

      <Modal open={!!detail} title={detail ? `${detail.toolId} · 分享内容` : undefined} onClose={() => setDetail(null)}>
        {detail ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div className="muted">
                创建：{fmt(detail.createdAt)}｜过期：{fmt(detail.expiresAt)}｜撤销：{fmt(detail.revokedAt)}
              </div>
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <button className="btn secondary" type="button" onClick={() => copyText(detail.shareUrl)}>
                  复制链接
                </button>
                <a className="btn secondary" href={detail.shareUrl} target="_blank" rel="noreferrer">
                  打开链接
                </a>
              </div>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn secondary"
                type="button"
                style={detailTab === 'preview' ? { borderColor: 'rgba(139, 92, 246, 0.55)', background: 'rgba(139, 92, 246, 0.16)' } : undefined}
                onClick={() => setDetailTab('preview')}
              >
                预览
              </button>
              <button
                className="btn secondary"
                type="button"
                style={detailTab === 'json' ? { borderColor: 'rgba(139, 92, 246, 0.55)', background: 'rgba(139, 92, 246, 0.16)' } : undefined}
                onClick={() => setDetailTab('json')}
              >
                JSON
              </button>
            </div>

            {detailTab === 'preview' ? (
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">内容预览</div>
                  <span className="badge">只读</span>
                </div>
                <iframe
                  title="share-preview"
                  srcDoc={enhanceSnapshotHtmlForPreview(detail.snapshotHtml)}
                  style={{ width: '100%', height: 520, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.04)' }}
                />
              </div>
            ) : (
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">Snapshot JSON</div>
                  <span className="badge">只读</span>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn secondary" type="button" onClick={() => copyText(detail.snapshotJson)}>
                    复制 JSON
                  </button>
                </div>
                <div style={{ marginTop: 10 }}>
                  {(() => {
                    try {
                      const obj = JSON.parse(detail.snapshotJson || '{}');
                      return <JsonTree value={obj} depth={0} />;
                    } catch {
                      return <pre>{detail.snapshotJson}</pre>;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal open={bulkOpen} title="批量操作" onClose={() => setBulkOpen(false)}>
        <div className="muted" style={{ lineHeight: 1.8 }}>
          已选择 {selectedCount} 条分享记录。
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">操作</div>
          <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value as any)}>
            <option value="REVOKE">批量撤销（链接失效，保留记录）</option>
            <option value="DELETE">批量删除（移除记录，不可恢复）</option>
          </select>
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">确认输入：{needConfirm}</div>
          <input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value.slice(0, 20))} placeholder={needConfirm} />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button
            className={bulkAction === 'DELETE' ? 'btn danger' : 'btn'}
            type="button"
            disabled={bulkRunning || !selectedCount || !confirmOk}
            onClick={async () => {
              const ids = selectedIds;
              if (!ids.length) return;
              setBulkRunning(true);
              setMsg(null);
              try {
                const url = bulkAction === 'DELETE' ? '/api/shares/bulk-delete' : '/api/shares/bulk-revoke';
                const r = await apiFetch<{ ok: boolean; items: Array<{ id: string; ok: boolean }> }>(url, { method: 'POST', body: JSON.stringify({ ids }) });
                const okN = (r.items || []).filter((x) => x.ok).length;
                const failN = (r.items || []).length - okN;
                setMsg(failN ? `批量完成：成功 ${okN}，失败 ${failN}` : `批量完成：成功 ${okN}`);
                setBulkOpen(false);
                setBulkConfirm('');
                await load();
              } catch {
                setMsg('批量执行失败，请稍后重试');
              } finally {
                setBulkRunning(false);
              }
            }}
          >
            {bulkRunning ? '执行中…' : '执行'}
          </button>
          <button className="btn secondary" type="button" onClick={() => setBulkOpen(false)}>
            取消
          </button>
        </div>
      </Modal>
    </div>
  );
}


